import React, { useCallback, useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Grid,
  Link,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { BsChevronDown } from "react-icons/bs";
import { Link as RouterLink } from "react-router-dom";

import { ErrorNotifier } from "../../common/error/ErrorNotifier";
import { useToast } from "../../common/hooks/useToast";
import { Popover } from "../../common/popover/Popover";
import { formatRetryAfter, getRateLimitRejection } from "../../common/rateLimit";
import { ColumnInterface, Table } from "../../common/table/Table";
import { config } from "../../config";
import { Item } from "../../styles/common";
import { BanMemberDialog } from "./BanMemberDialog";
import { useSetUserBanned } from "./useSetUserBanned";
import { BANNED_USERS, USER_BAN_SETTINGS } from "./userBans.queries";
import { SET_USER_BAN_ENABLED } from "./userBans.mutation";
import {
  BanTarget,
  BannedUserList,
  ENFORCEMENT_SCOPE_NOTICE,
  HALF_APPLIED_HINT,
  OTP_SEND_LOG_SQL,
  PROPAGATION_NOTICE,
  UserBanSettings,
  UserBanStatus,
  describeBanReason,
  describeBannedSince,
  describeContact,
  describeEnforcementFailure,
  describeLockout,
  formatBanTimestamp,
  isHalfApplied,
  lockoutStateOf,
} from "./userBan";

const perPage = config.rowsPerPage;

/**
 * Banned members: the list, the un-ban, and the enforcement switch.
 *
 * Banning happens from the Users screen, where an admin can actually find the
 * person. This screen is the other half — who is banned, whether the ban is
 * doing anything, and how to lift it — and it is the only screen that can show a
 * banned account at all, since a ban also marks the account deleted and the
 * ordinary list paths that hide deleted members would hide it.
 *
 * Nothing here polls. Both queries read the database directly rather than through
 * the 30-second cache `BanGuard` uses, so a refetch after a write is accurate —
 * unlike the rate-limit and country-blocking screens, where an immediate re-read
 * returns stale values and makes a successful save look failed.
 */
export const UserBans: React.FC = (): JSX.Element => {
  const toast = useToast();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(perPage);
  const [searchInput, setSearchInput] = useState("");
  /**
   * Applied on submit, not per keystroke: `search` is matched server-side across
   * username, email, phone and name, so every character would be a query over
   * the whole user table.
   */
  const [search, setSearch] = useState("");

  const [unbanTarget, setUnbanTarget] = useState<BanTarget | null>(null);
  const [confirmDisable, setConfirmDisable] = useState(false);
  const [savingEnabled, setSavingEnabled] = useState(false);

  const variables = useMemo(() => {
    const next: Record<string, unknown> = {
      skip: page * rowsPerPage,
      take: rowsPerPage,
    };

    // Only sent when there is one, so an empty box is not a filter on "".
    if (search) next.search = search;

    return next;
  }, [page, rowsPerPage, search]);

  const {
    data: listData,
    loading: listLoading,
    error: listError,
    refetch: refetchList,
  } = useQuery(BANNED_USERS, {
    variables,
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  const {
    data: settingsData,
    loading: settingsLoading,
    error: settingsError,
    refetch: refetchSettings,
  } = useQuery(USER_BAN_SETTINGS, {
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  const [setUserBanEnabled] = useMutation(SET_USER_BAN_ENABLED);

  const { setBanned, saving: savingBan } = useSetUserBanned();

  const list: BannedUserList | undefined = listData?.bannedUsers;
  const settings: UserBanSettings | undefined = settingsData?.userBanSettings;

  const rows: Array<UserBanStatus> = useMemo(() => list?.users ?? [], [list]);

  /** Rows marked banned that are not actually locked out. See HALF_APPLIED_HINT. */
  const halfApplied = useMemo(() => rows.filter(isHalfApplied), [rows]);

  const refreshAll = useCallback(() => {
    refetchList();
    refetchSettings();
  }, [refetchList, refetchSettings]);

  const applySearch = useCallback(() => {
    setPage(0);
    setSearch(searchInput.trim());
  }, [searchInput]);

  const handleUnban = useCallback(async () => {
    const target = unbanTarget;
    if (!target) return;

    setUnbanTarget(null);

    const status = await setBanned(target, false);

    // Refetched rather than patched: the un-banned row leaves this list
    // entirely, and both reads are uncached so they come back correct.
    if (status) refreshAll();
  }, [refreshAll, setBanned, unbanTarget]);

  const writeEnabled = useCallback(
    async (enabled: boolean) => {
      setSavingEnabled(true);

      try {
        const result = await setUserBanEnabled({ variables: { enabled } });

        if (!result.data?.setUserBanEnabled) {
          toast.error("The server did not confirm the enforcement change.");
          return;
        }

        toast.success(
          enabled
            ? `Ban enforcement is on. Banned members are refused on every operation. ${PROPAGATION_NOTICE}`
            : `Ban enforcement is off. Banned members are no longer refused up front — but they still cannot sign in or register their old details. ${PROPAGATION_NOTICE}`
        );

        refetchSettings();
      } catch (error) {
        // Rate-limit rejections are announced by the global Apollo error link.
        if (!getRateLimitRejection(error)) {
          toast.error(describeEnforcementFailure(error, enabled));
        }
      } finally {
        setSavingEnabled(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [refetchSettings, setUserBanEnabled]
  );

  const columns: Array<ColumnInterface> = useMemo(
    () => [
      /*
       * Every column opts out of sorting: `bannedUsers` takes no `orderBy` and
       * always answers newest ban first, so an arrow that reordered nothing
       * would be a lie about what the server did.
       */
      {
        label: "Member",
        key: "username",
        skipSorting: true,
        minWidth: "180px",
        rowRender: (row: UserBanStatus) => (
          <Stack spacing={0.25}>
            <Typography variant="body2">{row.username}</Typography>
            <Typography variant="caption" color="text.secondary">
              id {row.userId}
            </Typography>
          </Stack>
        ),
      },
      {
        label: "Contact",
        key: "email",
        skipSorting: true,
        minWidth: "240px",
        rowRender: (row: UserBanStatus) => describeContact(row),
      },
      {
        label: "Banned",
        key: "bannedAt",
        skipSorting: true,
        minWidth: "220px",
        rowRender: (row: UserBanStatus) => describeBannedSince(row),
      },
      {
        label: "Reason",
        key: "banReason",
        skipSorting: true,
        minWidth: "220px",
        rowRender: (row: UserBanStatus) => describeBanReason(row),
      },
      {
        label: "Lockout",
        key: "isDeleted",
        skipSorting: true,
        minWidth: "160px",
        rowRender: (row: UserBanStatus) => {
          const state = lockoutStateOf(row);

          return (
            <Tooltip
              title={
                state === "locked-out"
                  ? "Marked deleted as well, which is what refuses their sign-in and reserves their phone number and email."
                  : state === "self-deleted"
                  ? "The member deleted this account themselves before the ban. Un-banning cannot restore it."
                  : HALF_APPLIED_HINT
              }
            >
              <Chip
                size="small"
                variant="outlined"
                color={
                  state === "locked-out"
                    ? "error"
                    : state === "self-deleted"
                    ? "default"
                    : "warning"
                }
                label={describeLockout(row)}
              />
            </Tooltip>
          );
        },
      },
      {
        label: "",
        key: "action",
        skipSorting: true,
        textAlign: "right",
        minWidth: "120px",
        rowRender: (row: UserBanStatus) => (
          <Button
            size="small"
            variant="outlined"
            disabled={savingBan}
            onClick={() =>
              setUnbanTarget({
                userId: row.userId,
                username: row.username,
                email: row.email,
                phone: row.phone,
                isBanned: true,
                isAnonymized: row.isAnonymized,
              })
            }
          >
            Un-ban
          </Button>
        ),
      },
    ],
    [savingBan]
  );

  const listRejection = getRateLimitRejection(listError);

  if ((listLoading && !listData) || (settingsLoading && !settingsData)) {
    return (
      <Stack alignItems="center" spacing={2} sx={{ py: 8 }}>
        <CircularProgress size={28} />
        <Typography variant="body2" color="text.secondary">
          Loading banned members…
        </Typography>
      </Stack>
    );
  }

  if (listRejection) {
    return (
      <Box sx={{ flexGrow: 1 }}>
        <Item>
          <Alert severity="warning" sx={{ m: 2 }}>
            <AlertTitle>This panel hit a rate limit</AlertTitle>
            {listRejection.message}
            {listRejection.retryAfterSeconds !== undefined && (
              <> Try again in {formatRetryAfter(listRejection.retryAfterSeconds)}.</>
            )}
            <Box sx={{ mt: 2 }}>
              <Button variant="outlined" size="small" onClick={() => refreshAll()}>
                Try again
              </Button>
            </Box>
          </Alert>
        </Item>
      </Box>
    );
  }

  if (listError || settingsError) {
    return (
      <Box sx={{ flexGrow: 1 }}>
        <Item>
          <ErrorNotifier error={listError ?? settingsError} />
        </Item>
      </Box>
    );
  }

  /*
   * `total` counts every banned account and ignores `search`, so while a search
   * is applied it cannot be the pager's count — it would offer pages of matches
   * that do not exist. Paged instead against what has actually been seen, with
   * one extra when a full page came back so Next stays available.
   */
  const seen = page * rowsPerPage + rows.length;
  const pagerCount = search
    ? rows.length === rowsPerPage
      ? seen + 1
      : seen
    : list?.total ?? 0;

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Grid container spacing={2}>
        {/* ---------------------------------------------- Header */}
        <Grid item xs={12}>
          <Item>
            <Grid
              container
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                flexWrap="wrap"
                rowGap={1}
                sx={{ m: 2 }}
              >
                <Typography variant="h4">Banned members</Typography>

                <Chip
                  color={settings?.bannedCount ? "error" : "success"}
                  label={
                    settings?.bannedCount
                      ? `${settings.bannedCount} banned`
                      : "Nobody is banned"
                  }
                />
              </Stack>

              <Stack direction="row" spacing={2} alignItems="center" sx={{ m: 2 }}>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={listLoading || settingsLoading}
                  onClick={() => refreshAll()}
                >
                  {listLoading || settingsLoading ? "Refreshing…" : "Refresh"}
                </Button>
              </Stack>
            </Grid>
          </Item>
        </Grid>

        {/* ---------------------------------------------- Enforcement off */}
        {settings && !settings.enabled && (
          <Grid item xs={12}>
            <Alert severity="warning">
              <AlertTitle>Ban enforcement is off</AlertTitle>
              <Typography variant="body2">
                The global guard is not refusing banned callers, so the
                operations that carry no guard of their own — and the public ones
                — are answering them normally. Banned members still cannot sign
                in and still cannot register their old phone number or email:
                those checks read the deleted flag, which this switch does not
                touch.
              </Typography>
            </Alert>
          </Grid>
        )}

        {/* ---------------------------------------------- Half-applied bans */}
        {halfApplied.length > 0 && (
          <Grid item xs={12}>
            <Alert severity="warning">
              <AlertTitle>
                {halfApplied.length === 1
                  ? "One ban on this page is not locked out"
                  : `${halfApplied.length} bans on this page are not locked out`}
              </AlertTitle>
              <Typography variant="body2">
                {halfApplied.map((user) => user.username).join(", ")} —{" "}
                {HALF_APPLIED_HINT}
              </Typography>
            </Alert>
          </Grid>
        )}

        {/* ---------------------------------------------- What a ban does */}
        <Grid item xs={12} md={7}>
          <Alert severity="info" sx={{ height: "100%" }}>
            <AlertTitle>What a ban does</AlertTitle>
            <Typography variant="body2">
              A banned account is refused on <strong>every</strong> operation,
              cannot sign in, and drops out of leaderboards, member counts,
              nearby members and broadcast audiences. Their phone number and
              email <strong>stay reserved</strong>, so the same details cannot be
              registered again — that is the difference from a deletion, which
              releases them.
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              What it does not do: remove their reviews, recommendations or chat
              messages; stop a push notification aimed at them by id; or prevent
              them signing up again with a <strong>different</strong> number.
              Moderating content is a separate job, and re-registration is capped
              only by the signup rate limits.
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              To ban someone, find them on the{" "}
              <Link component={RouterLink} to="/users">
                Users
              </Link>{" "}
              screen — banned members stay listed there, so that is also where you
              can see who is banned. {PROPAGATION_NOTICE}
            </Typography>
          </Alert>
        </Grid>

        {/* ---------------------------------------------- The switch */}
        <Grid item xs={12} md={5}>
          <Item sx={{ p: 3, height: "100%" }}>
            <Typography variant="h5" sx={{ mb: 0.5 }}>
              Ban enforcement
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              The escape hatch for the guard, not a way to give access back.
            </Typography>

            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(settings?.enabled)}
                  disabled={savingEnabled || !settings}
                  inputProps={{ "aria-label": "Ban enforcement" }}
                  onChange={(event) => {
                    if (event.target.checked) {
                      writeEnabled(true);
                      return;
                    }

                    setConfirmDisable(true);
                  }}
                />
              }
              label={
                settings?.enabled
                  ? "Enforcement enabled"
                  : "Enforcement DISABLED"
              }
            />

            <Typography variant="body2" sx={{ mt: 1.5 }}>
              {ENFORCEMENT_SCOPE_NOTICE}
            </Typography>

            <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap" rowGap={1}>
              {settings?.usingDefault && (
                <Tooltip title="The setting has never been written, so the built-in default (enforcement on) is what is running.">
                  <Chip size="small" variant="outlined" label="Never changed" />
                </Tooltip>
              )}
              {settings?.updatedAt && (
                <Typography variant="caption" color="text.secondary">
                  Last changed {formatBanTimestamp(settings.updatedAt)}
                </Typography>
              )}
            </Stack>
          </Item>
        </Grid>

        {/* ---------------------------------------------- The list */}
        <Grid item xs={12}>
          <Item sx={{ p: 3 }}>
            <Typography variant="h5" sx={{ mb: 0.5 }}>
              Currently banned
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Newest ban first, as the server orders them.
            </Typography>

            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              flexWrap="wrap"
              rowGap={2}
              sx={{ mb: 2 }}
            >
              <TextField
                size="small"
                label="Search"
                placeholder="Username, email, phone or name"
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") applySearch();
                }}
                sx={{ minWidth: 280 }}
              />

              <Button variant="outlined" size="small" onClick={applySearch}>
                Search
              </Button>

              {search && (
                <>
                  <Button
                    size="small"
                    onClick={() => {
                      setSearchInput("");
                      setPage(0);
                      setSearch("");
                    }}
                  >
                    Clear
                  </Button>
                  <Typography variant="caption" color="text.secondary">
                    Matching “{search}”. The pager counts what has been loaded —
                    the server does not report how many accounts match.
                  </Typography>
                </>
              )}
            </Stack>

            <Table
              page={page}
              setPage={setPage}
              rowsPerPage={rowsPerPage}
              setRowsPerPage={setRowsPerPage}
              loading={listLoading}
              columns={columns}
              rows={rows}
              total={pagerCount}
            />
          </Item>
        </Grid>

        {/* ---------------------------------------------- Troubleshooting */}
        <Grid item xs={12}>
          <Accordion>
            <AccordionSummary expandIcon={<BsChevronDown />}>
              <Typography variant="h6">
                Someone says they are locked out, or never got their code
              </Typography>
            </AccordionSummary>

            <AccordionDetails>
              <Typography variant="body2" sx={{ mb: 1.5 }}>
                A banned member using the app sees{" "}
                <code>ACCOUNT_BANNED</code> — the account is suspended, and the
                app points them at support. That is deliberately not{" "}
                <code>ACCOUNT_DELETED</code>, which invites the person to
                register again with another number; a banned member must not be
                told that.
              </Typography>

              <Typography variant="body2" sx={{ mb: 1.5 }}>
                Asking for a verification code is different: it{" "}
                <strong>silently does nothing</strong>. No message is sent and no
                error is raised, because a distinguishable error there would be
                an "is this number registered" oracle. So the person reports a
                code that never arrived, and the send log is the only place the
                reason exists — <code>banned_account</code> for a ban,{" "}
                <code>deleted_account</code> for an account the member deleted
                themselves:
              </Typography>

              <Box
                component="pre"
                sx={{
                  p: 2,
                  m: 0,
                  overflowX: "auto",
                  fontSize: "0.8rem",
                  backgroundColor: "action.hover",
                  borderRadius: 1,
                }}
              >
                {OTP_SEND_LOG_SQL}
              </Box>

              <Typography variant="body2" sx={{ mt: 1.5 }}>
                If they are trying to sign up again on the same number they get{" "}
                <code>PHONE_ALREADY_IN_USE</code> (or{" "}
                <code>EMAIL_ALREADY_IN_USE</code>): the row still holds those
                keys, which is the point. A different number works — a ban is not
                evasion-proof.
              </Typography>

              <Typography variant="body2" sx={{ mt: 1.5 }}>
                If the ban should never have been applied, un-ban them here.
                Turning off Ban enforcement is not the fix: it would leave them
                unable to sign in anyway, and it would drop the guard for every
                other banned account at the same time.
              </Typography>
            </AccordionDetails>
          </Accordion>
        </Grid>
      </Grid>

      {/* ---------------------------------------------- Un-ban confirmation */}
      <BanMemberDialog
        open={Boolean(unbanTarget)}
        target={unbanTarget}
        saving={savingBan}
        onCancel={() => setUnbanTarget(null)}
        onConfirm={handleUnban}
      />

      {/* ---------------------------------------------- Switch-off confirmation */}
      <Popover
        open={confirmDisable}
        handleClose={() => setConfirmDisable(false)}
      >
        <Item sx={{ p: 3, maxWidth: 520, boxShadow: 6 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Turn ban enforcement off?
          </Typography>

          <Typography variant="body2" sx={{ mb: 2 }}>
            {ENFORCEMENT_SCOPE_NOTICE}
          </Typography>

          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              It applies to{" "}
              <strong>
                all {settings?.bannedCount ?? 0} banned account
                {settings?.bannedCount === 1 ? "" : "s"}
              </strong>{" "}
              at once, fleet-wide, within about 30 seconds. Reach for it if the
              guard itself is misbehaving — not to release one person.
            </Typography>
          </Alert>

          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button onClick={() => setConfirmDisable(false)}>Cancel</Button>
            <Button
              variant="contained"
              color="warning"
              onClick={() => {
                setConfirmDisable(false);
                writeEnabled(false);
              }}
            >
              Turn enforcement off
            </Button>
          </Stack>
        </Item>
      </Popover>
    </Box>
  );
};
